import { useState } from 'react';
import type { adminViews } from '@cwos/api-client';
import { usePermissions } from '../permissions.js';
import { downloadTextFile } from '../../lib/downloadCsv.js';

/**
 * Export button for the admin-view reporting surfaces.
 *
 * Renders ONLY when the principal holds `reporting:export` — an unheld
 * permission means the button is absent, never a grayed-out ghost
 * (default-deny, doc 13 T0). While exporting it shows an honest busy label;
 * failures surface inline; a truncated export is said out loud (the server
 * caps rows and flags it — never silently short data).
 */
export function ExportCsvButton({
  fallbackFilename,
  onExport,
  onDownloaded,
  label = 'Export CSV',
}: {
  fallbackFilename: string;
  onExport: () => Promise<adminViews.CsvExport>;
  /**
   * Called ONLY after the browser has actually been handed the file.
   *
   * This exists because the download is the last thing that can fail and the
   * only thing a caller's "last export" line is really about. A caller that
   * records the result inside `onExport` records it BEFORE `downloadTextFile`
   * runs, so a `URL.createObjectURL` that throws — a browser with object URLs
   * disabled, a blob the environment will not allocate — leaves the screen
   * saying "Last export: 412 rows" beside "Couldn't export", with no file
   * anywhere. The server's answer was true; the sentence about the file was
   * not, and the operator's next act is to go looking in Downloads for
   * something that was never written.
   */
  onDownloaded?: (result: adminViews.CsvExport) => void;
  label?: string;
}) {
  const { has } = usePermissions();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [truncated, setTruncated] = useState(false);

  if (!has('reporting:export')) return null;

  const run = async () => {
    setBusy(true);
    setError(null);
    setTruncated(false);
    try {
      const result = await onExport();
      // ORDER IS THE WHOLE POINT. Nothing may claim the export happened until
      // the file has been handed to the browser, so `downloadTextFile` runs
      // first and every statement about it — the truncation notice here, the
      // caller's "last export" line — comes after it returns. If it throws, the
      // catch below runs instead and the screen says only that it failed.
      downloadTextFile(result.filename ?? fallbackFilename, result.text);
      setTruncated(result.truncated);
      onDownloaded?.(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Export failed.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-10)', flexWrap: 'wrap' }}
    >
      <button type="button" className="rn-btn-quiet" disabled={busy} onClick={() => void run()}>
        {busy ? 'Exporting…' : label}
      </button>
      {error ? (
        <span className="rn-muted" role="alert">
          Couldn’t export: {error}
        </span>
      ) : null}
      {truncated ? (
        <span className="rn-muted" role="status">
          Export was capped at the maximum row count — narrow the period for a complete export.
        </span>
      ) : null}
    </div>
  );
}
